import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { getExceptionalStatusTranslationKey } from "src/utils/getExceptionalStatusTranslationKey";
import {
  LocationExceptionalStatusRead,
  LocationExceptionalStatusType,
} from "src/store/api/generatedApi";

dayjs.extend(utc);

const STATUS_DOT_COLOR: Record<LocationExceptionalStatusType, string> = {
  missing: "grey.900",
  ignorable: "grey.500",
  bad_labels: "warning.main",
  broken_scale_weights: "warning.main",
};

const DATE_TIME = "MM-DD HH:mm:ss";
const TIME_ONLY = "HH:mm:ss";

const formatRange = (startedAt: string, endedAt: string): string => {
  const start = dayjs.utc(startedAt);
  const end = dayjs.utc(endedAt);
  const sameDay = start.isSame(end, "day");
  const endFormatted = sameDay ? end.format(TIME_ONLY) : end.format(DATE_TIME);
  return `${start.format(DATE_TIME)} – ${endFormatted}`;
};

export const DayExceptionsList = ({
  statuses,
  onEdit,
  onDelete,
}: {
  statuses: LocationExceptionalStatusRead[];
  onEdit: (status: LocationExceptionalStatusRead) => void;
  onDelete: (id: number) => void;
}) => {
  const { t } = useTenantTranslation();

  if (statuses.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("noExceptionsForDay")}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {statuses.map((status) => (
        <Stack
          key={status.id}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ py: 0.25 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flexShrink: 0,
              bgcolor: STATUS_DOT_COLOR[status.status],
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" display="block" noWrap>
              {formatRange(status.started_at, status.ended_at)} ·{" "}
              {t(getExceptionalStatusTranslationKey(status.status))}
            </Typography>
            {status.note ? (
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                noWrap
              >
                {status.note}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              onEdit(status);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              onDelete(status.id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );
};
